/** Voice picker (components/VoicePicker/spec.md). `VoiceList` serves the first visit inline and
 *  the in-session popover. */
import {useState, useEffect, useRef, useCallback} from 'react'
import classNames from 'classnames'
import {Icon} from '@ui/Icon/icon'
import type {VoiceOption} from '@product/quies/data/voiceRoster'
import {voicesFor, type VoiceRoster} from '@product/quies/utils/voiceChoice'
import {usePopover} from '@product/quies/hooks/usePopover'
import {useInterfaceStrings} from '@product/quies/i18n/catalogue'
import {fill} from '@product/quies/i18n/strings'
import {quiesTrack} from '@product/quies/utils/analytics'
import styles from './VoicePicker.module.scss'

/** The preview route (`api.voice-preview.$language.$voiceId`) synthesizes a preview on first
 *  request and serves it after. */
export function previewUrlFor(language: string, voiceId: string): string {
    return `/api/voice-preview/${encodeURIComponent(language)}/${encodeURIComponent(voiceId)}`
}

interface VoiceListProps {
    language: string
    voices: VoiceOption[]
    voiceId: string | null
    onChange: (voiceId: string) => void
    disabled?: boolean
}

/** One preview at a time; a new preview stops the previous one. A failed preview returns to rest
 *  silently (spec § Behaviour). */
export function VoiceList({language, voices, voiceId, onChange, disabled}: VoiceListProps) {
    const {t} = useInterfaceStrings()
    const [previewing, setPreviewing] = useState<string | null>(null)
    const audioRef = useRef<HTMLAudioElement | null>(null)

    const stop = useCallback(() => {
        audioRef.current?.pause()
        audioRef.current = null
        setPreviewing(null)
    }, [])

    const play = useCallback((id: string) => {
        audioRef.current?.pause()
        const audio = new Audio(previewUrlFor(language, id))
        audioRef.current = audio
        setPreviewing(id)
        audio.addEventListener('ended', stop)
        audio.addEventListener('error', stop)
        audio.play().then(() => quiesTrack('voice_preview_played', {voice_id: id})).catch(stop)
    }, [language, stop])

    useEffect(() => () => { audioRef.current?.pause() }, [])

    return (
        <div role="radiogroup" aria-label={t.session.changeVoice} className={styles.voiceList}>
            {voices.map((v) => {
                const isPreviewing = previewing === v.voiceId
                return (
                    <div key={v.voiceId} className={classNames(styles.pill, {[styles.chosen]: v.voiceId === voiceId})}>
                        <button
                            type="button"
                            className={classNames(styles.preview, {[styles.previewing]: isPreviewing})}
                            onClick={() => (isPreviewing ? stop() : play(v.voiceId))}
                            aria-label={isPreviewing ? t.session.stopPreview : fill(t.session.previewVoice, {voice: v.label})}
                            disabled={disabled}
                        >
                            <Icon id={isPreviewing ? 'qs-pause-fill' : 'qs-play-fill'} size={14} />
                        </button>
                        <label className={styles.name}>
                            <input
                                type="radio"
                                name="quies-voice"
                                value={v.voiceId}
                                checked={v.voiceId === voiceId}
                                onChange={() => onChange(v.voiceId)}
                                disabled={disabled}
                            />
                            <span>{v.label}</span>
                            {v.voiceId === voiceId && <Icon id="qs-check" size={14} outline className={styles.check} />}
                        </label>
                    </div>
                )
            })}
        </div>
    )
}

interface VoicePickerProps {
    currentLanguage: string | null
    currentVoiceId: string | null
    onVoiceChange: (voiceId: string) => void
    voiceRoster: VoiceRoster
    disabled?: boolean
}

const ARROW_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'])

/** In-session trigger + popover/sheet (spec § Anatomy, § Behaviour: "the trigger is hidden when no
 *  language is chosen", as today). */
export function VoicePicker({currentLanguage, currentVoiceId, onVoiceChange, voiceRoster, disabled}: VoicePickerProps) {
    const {t} = useInterfaceStrings()
    const {open, setOpen, containerRef, triggerRef, popoverRef} = usePopover(disabled)
    // A native radio checks on an arrow key with the same `change` a click fires: arrows move
    // between voices, a click, Space or Enter chooses and closes (spec § Behaviour).
    const movedByArrowRef = useRef(false)
    const close = () => { setOpen(false); triggerRef.current?.focus() }

    const voices = voicesFor(voiceRoster, currentLanguage)
    const current = voices.find((v) => v.voiceId === currentVoiceId) ?? null

    if (!currentLanguage || voices.length === 0) return null

    return (
        <div ref={containerRef} className={styles.container}>
            <button
                ref={triggerRef}
                type="button"
                className={classNames(styles.trigger, {[styles.disabled]: disabled})}
                aria-label={t.session.changeVoice}
                aria-haspopup="dialog"
                aria-expanded={open}
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
            >
                <span className={styles.initial}>{current?.label?.[0] ?? <Icon id="qs-voice" size={18} outline />}</span>
                <span className={styles.triggerLabel}>{current?.label ?? ''}</span>
            </button>

            {open && (
                <div
                    ref={popoverRef}
                    className={styles.popover}
                    role="dialog"
                    aria-label={t.session.changeVoice}
                    onKeyDown={(e) => {
                        movedByArrowRef.current = ARROW_KEYS.has(e.key)
                        if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
                            // Its keypress would land on the refocused trigger and reopen the popover.
                            e.preventDefault()
                            close()
                        }
                    }}
                    onPointerDown={() => { movedByArrowRef.current = false }}
                >
                    <VoiceList
                        language={currentLanguage}
                        voices={voices}
                        voiceId={currentVoiceId}
                        onChange={(id) => {
                            onVoiceChange(id)
                            if (!movedByArrowRef.current) close()
                        }}
                    />
                </div>
            )}
        </div>
    )
}
